import type { Address, Hash, Hex } from 'viem';
import {
  AbiEventSignatureNotFoundError,
  concat,
  decodeEventLog,
  getEventSelector,
  keccak256,
  pad,
  slice,
  toHex,
  toRlp,
  type TransactionReceipt,
  trim,
} from 'viem';
import { DEPOSIT_TX_PREFIX, SourceHashDomain } from './types';

const TRANSACTION_DEPOSITED_ABI = [
  {
    type: 'event',
    anonymous: false,
    inputs: [
      {
        name: 'from',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'to',
        internalType: 'address',
        type: 'address',
        indexed: true,
      },
      {
        name: 'version',
        internalType: 'uint256',
        type: 'uint256',
        indexed: true,
      },
      {
        name: 'opaqueData',
        internalType: 'bytes',
        type: 'bytes',
        indexed: false,
      },
    ],
    name: 'TransactionDeposited',
  },
] as const;
const TRANSACTION_DEPOSITED_EVENT_HASH = getEventSelector(TRANSACTION_DEPOSITED_ABI[0]);

type GetDepositEventInfoFromTxReceiptParams = {
  receipt: TransactionReceipt;
};

type TransactionDepositedEvent = {
  eventName: string;
  args: Partial<{
    from: Address;
    to: Address;
    version: bigint;
    opaqueData: Hex;
  }>;
};

/**
 * @description Returns the TransactionDeposited event and log index, if found,
 * from the transaction receipt
 *
 * @param receipt the receipt of the transaction supposedly containing the TransactionDeposited event
 * @returns An array of L2 transaction hashes, corresponding to all TransactionDeposited events found in the transaction
 */
export function getDepositEventsInfoFromTxReceipt({
  receipt,
}: GetDepositEventInfoFromTxReceiptParams) {
  const depositEvents: { event: TransactionDepositedEvent; logIndex: number }[] = [];

  for (const l of receipt.logs) {
    if (l.topics[0] !== TRANSACTION_DEPOSITED_EVENT_HASH || l.logIndex === null) {
      continue;
    }
    try {
      const event = decodeEventLog({
        abi: TRANSACTION_DEPOSITED_ABI,
        data: l.data,
        topics: l.topics,
        eventName: 'TransactionDeposited',
      });
      depositEvents.push({ event, logIndex: l.logIndex });
    } catch (error) {
      if (!(error instanceof AbiEventSignatureNotFoundError)) {
        throw error;
      }
    }
  }
  return depositEvents;
}

type GetSourceHashParams = {
  domain: SourceHashDomain;
  logIndex: number;
  l1BlockHash: Hex;
};

/// from https://github.com/ethereum-optimism/optimism/blob/develop/packages/core-utils/src/optimism/deposit-transaction.ts#L117
/// with adaptions for viem
/// NOTE currently only supports user deposit txs, not L1InfoDeposit
export function getSourceHash({ domain, logIndex, l1BlockHash }: GetSourceHashParams) {
  const marker = toHex(logIndex);
  const input = concat([l1BlockHash, pad(marker, { size: 32 })]);
  const depositIDHash = keccak256(input);
  const domainHex = toHex(domain);
  const domainInput = concat([pad(domainHex, { size: 32 }), depositIDHash]);
  return keccak256(domainInput);
}

type GetL2HashFromDepositInfo = {
  event: TransactionDepositedEvent;
  logIndex: number;
  blockHash: Hash;
};

export function getL2HashFromL1DepositInfo({
  event,
  logIndex,
  blockHash,
}: GetL2HashFromDepositInfo) {
  /// code from https://github.com/ethereum-optimism/optimism/blob/develop/packages/core-utils/src/optimism/deposit-transaction.ts#L198
  /// with adaptions for viem
  const opaqueData = event.args.opaqueData;
  if (!opaqueData) throw new Error('No opaque data found in event');
  let offset = 0;
  const mint = slice(opaqueData, offset, offset + 32);
  offset += 32;
  const value = slice(opaqueData, offset, offset + 32);
  offset += 32;
  const gas = slice(opaqueData, offset, offset + 8);
  offset += 8;
  const isCreation = BigInt(opaqueData[offset] ?? '0') == 1n;
  offset += 1;
  const to = isCreation === true ? '0x' : event.args.to;
  if (!to) throw new Error('No to address found in event');
  const from = event.args.from;
  if (!from) throw new Error('No from address found in event');
  const length = opaqueData.length - offset;
  const data = slice(opaqueData, offset, offset + length);
  const domain = SourceHashDomain.UserDeposit;
  const l1BlockHash = blockHash;

  const sourceHash = getSourceHash({ domain, logIndex, l1BlockHash });

  const rlp = toRlp([
    sourceHash,
    from,
    to,
    trim(mint),
    trim(value),
    trim(gas),
    '0x', // for isSystemTransaction
    data,
  ]);

  return keccak256(concat([DEPOSIT_TX_PREFIX, rlp]));
}
